import { type FC, useState } from "react";
import { SenderEmailWarning } from "../../../../components/SenderEmailWarning";
import type {
  PromptType,
  Recipient,
  RecipientType,
  ReplyDraft,
} from "../../../../hooks/use-email-compose";
import { useSenderEmails } from "../../../../hooks/use-sender-emails-api";
import { ReplyConfirmModal } from "./ReplyConfirmModal";

// biome-ignore lint/style/useNamingConvention: API prompt_type values
const PROMPT_TYPE_LABELS: Record<PromptType, string> = {
  reply_matter: "案件への返信",
  reply_hr: "人材への返信",
  inquiry: "詳細問い合わせ",
  negotiate: "単価交渉",
  schedule: "日程調整",
  decline: "お断り",
  followup: "フォローアップ",
  custom: "カスタム指示",
};

export type ReplyComposePaneProps = {
  draft: ReplyDraft;
  isGenerating: boolean;
  isSending: boolean;
  error: string | null;
  onFieldChange: <K extends keyof ReplyDraft>(
    field: K,
    value: ReplyDraft[K],
  ) => void;
  onAddRecipient: (recipient: Recipient) => void;
  onRemoveRecipient: (email: string, type: RecipientType) => void;
  onGenerateBody: (promptType: PromptType, freeText?: string) => void;
  onSend: () => Promise<boolean>;
  onClose: () => void;
  onCancelGeneration: () => void;
};

/**
 * 返信メール作成ペイン
 * @param {ReplyComposePaneProps} props
 * @return {JSX.Element}
 */
export const ReplyComposePane: FC<ReplyComposePaneProps> = ({
  draft,
  isGenerating,
  isSending,
  error,
  onFieldChange,
  onAddRecipient,
  onRemoveRecipient,
  onGenerateBody,
  onSend,
  onClose,
  onCancelGeneration,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [newCcEmail, setNewCcEmail] = useState("");
  const [selectedPromptType, setSelectedPromptType] =
    useState<PromptType>("reply_matter");
  const [customFreeText, setCustomFreeText] = useState("");

  const { data: senderEmailsData } = useSenderEmails({ verifiedOnly: true });
  const senderEmails = senderEmailsData?.items ?? [];

  const hasSender = senderEmails.length > 0 || !!draft.fromEmail;
  const toRecipients = draft.recipients.filter(r => r.type === "to");
  const ccRecipients = draft.recipients.filter(r => r.type === "cc");
  const bccRecipients = draft.recipients.filter(r => r.type === "bcc");

  /**
   * CC/BCCアドレスを追加する
   * @param {RecipientType} type - 宛先種別
   * @return {void}
   */
  const handleAddCcBcc = (type: RecipientType) => {
    const trimmed = newCcEmail.trim();
    if (!trimmed) return;
    onAddRecipient({ email: trimmed, name: "", type });
    setNewCcEmail("");
  };

  /**
   * 送信確認を開く
   * @return {void}
   */
  const handleOpenConfirm = () => {
    setShowConfirm(true);
  };

  /**
   * 送信を実行
   * @return {Promise<void>}
   */
  const handleConfirmSend = async () => {
    setShowConfirm(false);
    await onSend();
  };

  /**
   * AI生成を実行
   * @return {void}
   */
  const handleGenerate = () => {
    onGenerateBody(
      selectedPromptType,
      selectedPromptType === "custom" ? customFreeText : undefined,
    );
  };

  return (
    <div className="card bg-base-100 shadow-md">
      <div className="card-body space-y-3">
        {/* ヘッダー */}
        <div className="flex items-center justify-between">
          <h3 className="card-title text-base">返信メール作成</h3>
          <button
            className="btn btn-ghost btn-xs"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="alert alert-error text-sm py-2">
            <span>{error}</span>
          </div>
        )}

        {/* 送信元 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">送信元</span>
          </label>
          {senderEmails.length === 0 && !draft.fromEmail ? (
            <SenderEmailWarning />
          ) : (
            <select
              className="select select-bordered select-sm w-full"
              value={draft.fromEmail}
              onChange={e => {
                onFieldChange("fromEmail", e.target.value);
                const sender = senderEmails.find(
                  s => s.email === e.target.value,
                );
                if (sender) {
                  onFieldChange("fromName", sender.displayName);
                }
              }}
            >
              {senderEmails.length === 0 && draft.fromEmail && (
                <option value={draft.fromEmail}>
                  {draft.fromName} ({draft.fromEmail})
                </option>
              )}
              {senderEmails.map(s => (
                <option key={s.email} value={s.email}>
                  {s.displayName} ({s.email})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* 宛先 (To) */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">宛先 (To)</span>
          </label>
          <div className="space-y-1">
            {toRecipients.map(r => (
              <div
                key={r.email}
                className="input input-bordered input-sm w-full bg-gray-50 flex items-center text-sm text-gray-600"
              >
                {r.name ? `${r.name} <${r.email}>` : r.email}
              </div>
            ))}
          </div>
          {/* CC/BCC トグル */}
          {!showCcBcc && (
            <button
              className="btn btn-ghost btn-xs mt-1 text-blue-500"
              onClick={() => setShowCcBcc(true)}
              type="button"
            >
              + CC/BCC を追加
            </button>
          )}
        </div>

        {/* CC/BCC セクション */}
        {showCcBcc && (
          <>
            {/* CC */}
            <div>
              <label className="label py-0">
                <span className="label-text text-xs">CC</span>
              </label>
              <div className="space-y-1">
                {ccRecipients.map(r => (
                  <div key={r.email} className="flex items-center gap-1">
                    <span className="input input-bordered input-sm flex-1 bg-gray-50 flex items-center text-sm text-gray-600">
                      {r.email}
                    </span>
                    <button
                      className="btn btn-ghost btn-xs text-red-400"
                      onClick={() => onRemoveRecipient(r.email, "cc")}
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div className="flex gap-1">
                  <input
                    type="email"
                    className="input input-bordered input-sm flex-1"
                    placeholder="cc@example.com"
                    value={newCcEmail}
                    onChange={e => setNewCcEmail(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCcBcc("cc");
                      }
                    }}
                  />
                  <button
                    className="btn btn-ghost btn-sm btn-square"
                    onClick={() => handleAddCcBcc("cc")}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* BCC */}
            <div>
              <label className="label py-0">
                <span className="label-text text-xs">BCC</span>
              </label>
              <div className="space-y-1">
                {bccRecipients.map(r => (
                  <div key={r.email} className="flex items-center gap-1">
                    <span className="input input-bordered input-sm flex-1 bg-gray-50 flex items-center text-sm text-gray-600">
                      {r.email}
                    </span>
                    <button
                      className="btn btn-ghost btn-xs text-red-400"
                      onClick={() => onRemoveRecipient(r.email, "bcc")}
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div className="flex gap-1">
                  <input
                    type="email"
                    className="input input-bordered input-sm flex-1"
                    placeholder="bcc@example.com"
                    value={newCcEmail}
                    onChange={e => setNewCcEmail(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCcBcc("bcc");
                      }
                    }}
                  />
                  <button
                    className="btn btn-ghost btn-sm btn-square"
                    onClick={() => handleAddCcBcc("bcc")}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 件名 */}
        <div>
          <label className="label py-0">
            <span className="label-text text-xs">件名</span>
          </label>
          <input
            type="text"
            className="input input-bordered input-sm w-full"
            value={draft.subject}
            onChange={e => onFieldChange("subject", e.target.value)}
          />
        </div>

        {/* AI生成セクション */}
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-blue-700">
              AI文面生成
            </span>
          </div>
          <div className="flex gap-2 items-end">
            <select
              className="select select-bordered select-xs flex-1"
              value={selectedPromptType}
              onChange={e =>
                setSelectedPromptType(e.target.value as PromptType)
              }
            >
              {Object.entries(PROMPT_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <button
              className="btn btn-accent btn-xs"
              onClick={handleGenerate}
              disabled={isGenerating}
              type="button"
            >
              {isGenerating ? (
                <>
                  <span className="loading loading-spinner loading-xs" />
                  生成中
                </>
              ) : (
                "生成"
              )}
            </button>
          </div>
          {selectedPromptType === "custom" && (
            <textarea
              className="textarea textarea-bordered w-full text-xs mt-2"
              rows={2}
              placeholder="カスタム指示を入力..."
              value={customFreeText}
              onChange={e => setCustomFreeText(e.target.value)}
            />
          )}
        </div>

        {/* 本文 */}
        <div className="flex-1">
          <label className="label py-0">
            <span className="label-text text-xs">
              本文
              {isGenerating && (
                <span className="ml-2 text-blue-500">
                  <span className="loading loading-dots loading-xs" />{" "}
                  AI生成中...
                </span>
              )}
            </span>
          </label>
          <textarea
            className="textarea textarea-bordered w-full text-sm"
            rows={12}
            value={draft.body}
            onChange={e => onFieldChange("body", e.target.value)}
            placeholder={
              isGenerating
                ? "AIがメール本文を生成しています..."
                : "本文を入力..."
            }
          />
        </div>

        {/* ボタン群 */}
        <div className="flex justify-between items-center">
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            type="button"
          >
            キャンセル
          </button>
          <div className="flex gap-2">
            {isGenerating && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={onCancelGeneration}
                type="button"
              >
                生成停止
              </button>
            )}
            <button
              className="btn btn-primary btn-sm"
              onClick={handleOpenConfirm}
              disabled={
                isGenerating || isSending || !draft.body.trim() || !hasSender
              }
              type="button"
            >
              {isSending ? (
                <>
                  <span className="loading loading-spinner loading-xs" />
                  送信中...
                </>
              ) : (
                "送信"
              )}
            </button>
          </div>
        </div>

        {/* 確認モーダル */}
        <ReplyConfirmModal
          isOpen={showConfirm}
          subject={draft.subject}
          body={draft.body}
          recipients={draft.recipients}
          fromName={draft.fromName}
          onConfirm={handleConfirmSend}
          onCancel={() => setShowConfirm(false)}
        />
      </div>
    </div>
  );
};
